/**
 * Deciding what a paste MEANS, in one place.
 *
 * Every paste-to-attach surface — the entity detail zone, the upload controls,
 * the create dialogs — has to answer the same two questions: is this clipboard
 * carrying files the user meant to attach, and what should the file be called?
 * Answering them per-surface is how they drift apart, so they live here and
 * `usePasteFilesInto` is the only thing that calls them.
 */

/**
 * Clipboard names a pasted bitmap generically — every macOS screenshot arrives as
 * `image.png`, so a file list full of them is unreadable. Anything the OS did name
 * (a file copied from Finder/Explorer) keeps its name.
 */
const GENERIC_CLIPBOARD_NAMES = new Set(['', 'image', 'image.png', 'image.jpg', 'image.jpeg', 'image.webp']);

const MIME_EXTENSIONS: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'image/bmp': 'bmp',
  'image/tiff': 'tiff',
};

/**
 * Give a generically-named clipboard bitmap a sortable, human-readable filename.
 * The stamp is a filesystem identifier, not a displayed date — it is deliberately
 * a fixed UTC pattern rather than a locale format, so two users looking at the
 * same tenant's files see the same name.
 */
export function namePastedImage(file: File, now: Date = new Date()): File {
  if (!GENERIC_CLIPBOARD_NAMES.has(file.name.toLowerCase())) return file;
  const extension = MIME_EXTENSIONS[file.type] ?? 'png';
  const stamp = now.toISOString().slice(0, 19).replace(/[:T]/g, '-');
  return new File([file], `screenshot-${stamp}.${extension}`, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

/**
 * Decide whether a paste is "a file the user meant to attach" or "rich text that
 * happens to carry a bitmap rendition".
 *
 * Copying a table out of Word/Excel/Google Sheets puts `text/plain` + `text/html`
 * on the clipboard *alongside* an `image/png` rendition of the selection. Treating
 * that as an attachment would silently attach a picture of a spreadsheet every
 * time someone pasted formatted text into a description. A real screenshot carries
 * no text flavour at all, so the presence of a text flavour is the discriminator.
 */
export function clipboardCarriesText(types: readonly string[] | undefined): boolean {
  if (!types) return false;
  return types.some((type) => type === 'text/plain' || type === 'text/html');
}

/** Files a paste is offering, already renamed. Empty when the paste is really text. */
export function filesFromClipboard(data: DataTransfer | null): File[] {
  if (!data) return [];
  if (clipboardCarriesText(data.types)) return [];
  const files: File[] = [];
  for (const item of data.items) {
    if (item.kind !== 'file') continue;
    const file = item.getAsFile();
    if (file) files.push(namePastedImage(file));
  }
  return files;
}
